import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const contactInfo = [
  {
    icon: "ri-mail-line",
    title: "Email",
    value: "hello@klconsultinggroup.com",
    description: "For general inquiries and project discussions"
  },
  {
    icon: "ri-phone-line",
    title: "Phone",
    value: "+1 (555) 123-4567",
    description: "Business hours: Mon-Fri 9AM-6PM EST"
  },
  {
    icon: "ri-map-pin-line",
    title: "Office",
    value: "New York, NY",
    description: "Virtual consultations available worldwide"
  }
];

const offices = [
  {
    city: "New York",
    address: "123 Business District\nNew York, NY 10001",
    phone: "+1 (555) 123-4567",
    email: "ny@klconsultinggroup.com"
  },
  {
    city: "Washington DC",
    address: "456 Government Square\nWashington, DC 20001",
    phone: "+1 (555) 987-6543",
    email: "dc@klconsultinggroup.com"
  },
  {
    city: "Remote Services",
    address: "Serving clients globally\nthrough virtual consultations",
    phone: "+1 (555) 123-4567",
    email: "remote@klconsultinggroup.com"
  }
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('company', formData.company || '');
      formDataToSubmit.append('phone', formData.phone || '');
      formDataToSubmit.append('project_type', formData.projectType);
      formDataToSubmit.append('budget', formData.budget || '');
      formDataToSubmit.append('timeline', formData.timeline || '');
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5r551k98t1oshnre4eg', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          projectType: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-stone-900 mb-6">
              Let's Start the Conversation
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 font-light leading-relaxed mb-12">
              Ready to transform your business? We're here to discuss your challenges, explore solutions, and create a strategic roadmap for success.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div
                key={info.title}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 text-center group"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-yellow-600 group-hover:text-white transition-all duration-300">
                  <i className={`${info.icon} text-2xl text-yellow-600 group-hover:text-white`}></i>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-3">
                  {info.title}
                </h3>
                <p className="text-lg font-medium text-stone-900 mb-2">
                  {info.value}
                </p>
                <p className="text-stone-600 text-sm">
                  {info.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Form */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                Tell Us About Your Project
              </h2>
              <p className="text-stone-600 mb-8">
                Share your vision, challenges, and goals. We'll respond within 24 hours with insights and next steps.
              </p>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-200 text-green-800 p-4 rounded-lg mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-check-circle-line text-xl"></i>
                    <div>
                      <p className="font-medium">Message sent successfully!</p>
                      <p className="text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-error-warning-line text-xl"></i>
                    <div>
                      <p className="font-medium">Something went wrong</p>
                      <p className="text-sm">Please try again or contact us directly.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-stone-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-stone-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                      placeholder="your.email@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-stone-900 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-stone-900 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-stone-900 mb-2">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select project type</option>
                    <option value="digital-strategy">Digital Strategy & Consulting</option>
                    <option value="pmo-implementation">PMO Implementation</option>
                    <option value="website-development">Website Development</option>
                    <option value="process-optimization">Process Optimization & ISO</option>
                    <option value="digital-marketing">Digital Marketing & SEO</option>
                    <option value="pm-mentorship">PM Launchpad Mentorship</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-stone-900 mb-2">
                      Project Budget
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    >
                      <option value="">Select budget range</option>
                      <option value="10k-25k">$10K - $25K</option>
                      <option value="25k-50k">$25K - $50K</option>
                      <option value="50k-100k">$50K - $100K</option>
                      <option value="100k-250k">$100K - $250K</option>
                      <option value="250k+">$250K+</option>
                      <option value="not-sure">Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-stone-900 mb-2">
                      Desired Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    >
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (1-2 weeks)</option>
                      <option value="1-3-months">1-3 months</option>
                      <option value="3-6-months">3-6 months</option>
                      <option value="6-12-months">6-12 months</option>
                      <option value="12-months+">12+ months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-stone-900 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Tell us about your project goals, challenges, and what success looks like to you..."
                  />
                  <p className="text-xs text-stone-500 mt-2">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-stone-400 text-white py-4 rounded-full font-medium transition-all transform hover:scale-105 disabled:transform-none whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Sending Message...
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>

            {/* Sidebar Info */}
            <div>
              <div className="bg-stone-900 p-8 md:p-12 rounded-2xl text-white mb-8">
                <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-6">
                  What Happens Next?
                </h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      1
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Initial Review</h4>
                      <p className="text-stone-300 text-sm">We review your project details and prepare strategic insights.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      2
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Discovery Call</h4>
                      <p className="text-stone-300 text-sm">30-minute consultation to discuss your vision and challenges.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      3
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Strategic Proposal</h4>
                      <p className="text-stone-300 text-sm">Customized solution with timeline, deliverables, and investment.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Locations */}
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-6">
                  Office Locations
                </h3>
                <div className="space-y-6">
                  {offices.map((office, index) => (
                    <div key={office.city} className="border-b border-stone-200 last:border-b-0 pb-6 last:pb-0">
                      <h4 className="font-bold text-stone-900 mb-2">{office.city}</h4>
                      <div className="text-stone-600 text-sm space-y-1">
                        <p className="whitespace-pre-line">{office.address}</p>
                        <p>{office.phone}</p>
                        <p>{office.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-stone-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-8">
            {[
              {
                question: "How do you approach new client projects?",
                answer: "Every engagement begins with a comprehensive discovery phase where we understand your business objectives, current challenges, and success metrics. We then develop a tailored strategy that aligns with your organizational culture and resource constraints."
              },
              {
                question: "What industries do you specialize in?",
                answer: "We serve clients across healthcare, financial services, government, technology, and professional services. Our methodology-driven approach allows us to deliver value regardless of industry while bringing cross-sector insights to every engagement."
              },
              {
                question: "Do you work with remote teams?",
                answer: "Absolutely. We've successfully managed virtual teams and remote implementations since 2020. Our digital-first approach ensures seamless collaboration and project delivery regardless of geographic boundaries."
              },
              {
                question: "What's the typical project timeline?",
                answer: "Project timelines vary based on scope and complexity. Digital strategy projects typically range from 6-12 weeks, while comprehensive PMO implementations can span 3-6 months. We always provide detailed timelines during our proposal phase."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-stone-50 p-8 rounded-2xl">
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-4">
                  {faq.question}
                </h3>
                <p className="text-stone-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}